import { Link, useLocation } from 'react-router-dom';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Bell,
  Bot,
  Briefcase,
  Building2,
  CalendarDays,
  ChevronRight,
  ClipboardCheck,
  LayoutDashboard,
  Menu,
  Radar,
  Target,
  Users,
  Video,
  X,
  History,
  RotateCcw,
  Settings2,
  Sparkles,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const INFRA_ADMIN_EMAILS = ['demo@bidsignaliq.com'];
const SIDEBAR_ACTIVE_BG = 'rgba(255, 255, 255, 0.09)';
const SIDEBAR_ACTIVE_HOVER_BG = 'rgba(255, 255, 255, 0.13)';
const SIDEBAR_HOVER_BG = 'rgba(255, 255, 255, 0.06)';

const SECTION_LABEL_STYLE = {
  fontSize: '10px',
  fontWeight: 600,
  letterSpacing: '0.08em',
  color: '#94A3B8',
  textTransform: 'uppercase',
  padding: '16px 16px 4px 16px',
  cursor: 'default',
};

const itemBaseStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 12px',
  margin: '1px 8px',
  borderRadius: '6px',
  fontSize: '14px',
  color: '#CBD5E1',
  cursor: 'pointer',
  textDecoration: 'none',
  background: 'transparent',
  border: 'none',
  width: 'calc(100% - 16px)',
  transition: 'background-color 150ms ease, color 150ms ease',
};

const sidebarSections = [
  {
    label: 'COMMAND',
    items: [
      { type: 'link', label: 'Command Center', path: '/command-center', icon: LayoutDashboard, exact: true },
      { type: 'link', label: 'Atlas', path: '/atlas', icon: Sparkles, exact: true },
    ],
  },
  {
    label: 'FIND',
    items: [
      {
        type: 'group',
        label: 'Federal Opportunities',
        key: 'federal-opportunities',
        icon: Radar,
        children: [
          { label: 'All Opportunities', path: '/missions?tab=full_market&jurisdiction=Federal', exact: true, queryMatch: ['tab=full_market', 'jurisdiction=Federal'] },
          { label: 'My Opportunities', path: '/missions?tab=pipeline&jurisdiction=Federal', exact: true, queryMatch: ['tab=pipeline', 'jurisdiction=Federal'] },
          { label: 'Forecast Opportunities', path: '/missions?tab=forecast&jurisdiction=Federal', exact: true, queryMatch: ['tab=forecast', 'jurisdiction=Federal'] },
        ],
      },
      {
        type: 'group',
        label: 'State & Local',
        key: 'state-opportunities',
        icon: Building2,
        children: [
          { label: 'All Opportunities', path: '/missions?tab=full_market&jurisdiction=State', exact: true, queryMatch: ['tab=full_market', 'jurisdiction=State'] },
          { label: 'My Opportunities', path: '/missions?tab=pipeline&jurisdiction=State', exact: true, queryMatch: ['tab=pipeline', 'jurisdiction=State'] },
        ],
      },
      { type: 'link', label: 'Signal Intelligence', path: '/intelligence', icon: Radar, badgeKey: 'intelligence', exact: true },
      { type: 'link', label: 'Conversion Tracker', path: '/conversion-tracker', icon: Target, exact: true },
      { type: 'link', label: 'Recompete Watchlist', path: '/recompete', icon: RotateCcw, exact: true },
    ],
  },
  {
    label: 'PURSUE',
    items: [
      {
        type: 'group',
        label: 'Pipeline',
        key: 'pipeline',
        icon: LayoutDashboard,
        children: [
          { label: 'All Missions', path: '/missions', exact: true },
          { label: 'Assigned to Me', path: '/missions?filter=me', exact: true, queryMatch: 'filter=me' },
          { label: 'Submitted', path: '/missions?tab=submitted', exact: true, queryMatch: 'tab=submitted' },
          { label: 'No Bid', path: '/missions?tab=no_bid', exact: true, queryMatch: 'tab=no_bid' },
        ],
      },
      { type: 'link', label: 'Amendment Intelligence', path: '/amendments', icon: Bell, badgeKey: 'amendments', exact: true },
      { type: 'link', label: 'Reports', path: '/reports', icon: ClipboardCheck, exact: true },
      { type: 'link', label: 'Lost Contracts', path: '/lost-contracts', icon: History, exact: true },
    ],
  },
  {
    label: 'TEAM',
    items: [
      {
        type: 'group',
        label: 'Teaming',
        key: 'teaming',
        icon: Users,
        children: [
          { label: 'Campaigns & Partners', path: '/teaming-partners', icon: Users, exact: true },
          { label: 'Shared Workspaces', path: '/teaming/workspaces', icon: LayoutDashboard, exact: true },
        ],
      },
    ],
  },
  {
    label: 'EXECUTE',
    items: [
      { type: 'link', label: 'Pricing', path: '/pricing', icon: Briefcase, restrictedForViewer: true, exact: true },
      { type: 'link', label: 'Proposal Library', path: '/proposal-library', icon: ClipboardCheck, exact: true },
      { type: 'link', label: 'Post-Award', path: '/post-award', icon: Briefcase, badgeKey: 'post_award' },
      { type: 'link', label: 'Lessons Learned', path: '/lessons', icon: ClipboardCheck, exact: true },
    ],
  },
  {
    label: 'INTELLIGENCE',
    items: [
      {
        type: 'group',
        label: 'Market Intelligence',
        key: 'market-intelligence',
        icon: Users,
        children: [
          { type: 'label', label: 'COMPANY' },
          { label: 'Company Intelligence', path: '/company', icon: Building2, exact: true },
          { label: 'Contacts', path: '/contacts', icon: Users, exact: true },
          { label: 'BD Tasks', path: '/bd-tasks', icon: ClipboardCheck, exact: true },
          // TODO: /company/awards not defined yet
          // TODO: /intelligence/naics not defined yet
          { type: 'label', label: 'GOVERNMENT' },
          { label: 'Agencies', path: '/agencies', icon: Building2, exact: true },
          { label: 'Contracting Officers', path: '/intelligence/contracting-officers', icon: Users, exact: true },
          { label: 'Contractors', path: '/intelligence/contractors', icon: Building2, exact: true },
        ],
      },
      { type: 'link', label: 'Atlas Operations', path: '/agents-console', icon: Bot, exact: true },
      { type: 'link', label: 'Morning Briefing', path: '/morning', icon: CalendarDays, exact: true },
      { type: 'link', label: 'Autonomy & Approvals', path: '/settings/atlas/autonomy', icon: Settings2, exact: true },
      { type: 'link', label: 'GovCon Webinars', path: '/webinars', icon: Video, exact: true },
    ],
  },
];

const isActiveForLocation = (location, path, exact = false, queryMatch = null) => {
  const currentPath = location.pathname;
  const currentSearch = location.search;

  if (exact && queryMatch) {
    const matches = Array.isArray(queryMatch) ? queryMatch : [queryMatch];
    return currentPath === path && matches.every((part) => currentSearch.includes(part));
  }
  if (exact) {
    return currentPath === path && currentSearch === '';
  }
  return currentPath.startsWith(path) && path !== '/';
};

const getGroupForRoute = (location) => {
  if (location.pathname === '/missions' && /jurisdiction=(Federal|State)/i.test(location.search)) {
    return location.search.toLowerCase().includes('jurisdiction=state') ? 'state-opportunities' : 'federal-opportunities';
  }
  if (isActiveForLocation(location, '/missions', true) || isActiveForLocation(location, '/missions', true, 'filter=me') || ['submitted', 'no_bid'].some((tab) => location.search.includes(`tab=${tab}`))) return 'pipeline';
  if (location.pathname.startsWith('/teaming-partners') || location.pathname.startsWith('/teaming/workspace')) return 'teaming';
  if (location.pathname.startsWith('/company') || location.pathname.startsWith('/contacts') || location.pathname.startsWith('/bd-tasks') || location.pathname.startsWith('/agencies') || location.pathname.startsWith('/intelligence/contracting-officers') || location.pathname.startsWith('/intelligence/contractors')) return 'market-intelligence';
  return null;
};

const NavLinkItem = ({ item, active, badgeCount = 0, onClick }) => {
  const Icon = item.icon;
  return (
    <Link
      to={item.path}
      onClick={onClick}
      className="bsiq-sidebar-item"
      style={{
        ...itemBaseStyle,
        background: active ? SIDEBAR_ACTIVE_BG : 'transparent',
        color: active ? '#FFFFFF' : '#CBD5E1',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = active ? SIDEBAR_ACTIVE_HOVER_BG : SIDEBAR_HOVER_BG;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = active ? SIDEBAR_ACTIVE_BG : 'transparent';
      }}
      data-testid={`nav-${item.path.replace(/[/?=&]/g, '-').replace(/^-+/, '')}`}
    >
      <span className="flex min-w-0 items-center gap-2.5">
        {Icon && <Icon className="h-4 w-4 shrink-0" />}
        <span className="truncate">{item.label}</span>
      </span>
      {badgeCount > 0 && (
        <span
          className="ml-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[10px] font-bold"
        style={{ background: active ? '#FFFFFF' : '#CBD5E1', color: '#0D1F3C' }}
        >
          {badgeCount > 99 ? '99+' : badgeCount}
        </span>
      )}
    </Link>
  );
};

const AccordionItem = ({ item, isOpen, activeChildPath, isActive, onToggle, onNavigate }) => {
  const Icon = item.icon;

  return (
    <div>
      <button
        type="button"
        className="bsiq-sidebar-item"
        style={{
          ...itemBaseStyle,
          background: 'transparent',
          color: '#CBD5E1',
        }}
        onClick={onToggle}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = SIDEBAR_HOVER_BG;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
        }}
        aria-expanded={isOpen}
        data-testid={`nav-group-${item.key}`}
      >
        <span className="flex min-w-0 items-center gap-2.5">
          {Icon && <Icon className="h-4 w-4 shrink-0" />}
          <span className="truncate">{item.label}</span>
        </span>
        <ChevronRight
          className="h-4 w-4 shrink-0"
          style={{
            transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 150ms ease',
          }}
        />
      </button>

      <div
        style={{
          overflow: 'hidden',
          maxHeight: isOpen ? '300px' : '0',
          transition: 'max-height 200ms ease',
        }}
      >
        <div className="py-1">
          {item.children.map((child) => {
            if (child.type === 'label') {
              return (
                <div
                  key={`${item.key}-${child.label}`}
                  style={{
                    margin: '8px 8px 2px 20px',
                    padding: '4px 12px 2px 32px',
                    fontSize: '10px',
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    color: '#6B7280',
                  }}
                >
                  {child.label}
                </div>
              );
            }
            const targetPath = child.path.split('?')[0];
            const active = isActive(targetPath, child.exact, child.queryMatch);
            return (
              <Link
                key={child.path}
                to={child.path}
                onClick={onNavigate}
                className="bsiq-sidebar-subitem block truncate"
                style={{
                  margin: '1px 8px 1px 20px',
                  padding: '7px 12px 7px 32px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: 600,
                    color: active ? '#FFFFFF' : '#AAB6C7',
                  background: active ? SIDEBAR_ACTIVE_BG : 'transparent',
                  textDecoration: 'none',
                  transition: 'background-color 150ms ease, color 150ms ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = active ? SIDEBAR_ACTIVE_HOVER_BG : SIDEBAR_HOVER_BG;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = active ? SIDEBAR_ACTIVE_BG : 'transparent';
                }}
                data-testid={`nav-${child.path.replace(/[/?=&]/g, '-').replace(/^-+/, '')}`}
              >
                {child.label}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const Sidebar = ({ isMobile = false, mobileOpen = false, onMobileToggle, onMobileClose }) => {
  const { user, isViewer } = useAuth();
  const location = useLocation();
  const pathname = location.pathname;
  const search = location.search;
  const [openGroup, setOpenGroup] = useState(null);
  const [badges, setBadges] = useState({ amendments: 0, intelligence: 0, post_award: 0 });
  const sidebarRef = useRef(null);
  const isActive = (path, exact = false, queryMatch = null) => {
    const currentPath = pathname;
    const currentSearch = search;

    if (exact && queryMatch) {
      const matches = Array.isArray(queryMatch) ? queryMatch : [queryMatch];
      return currentPath === path && matches.every((part) => currentSearch.includes(part));
    }
    if (exact) {
      return currentPath === path && currentSearch === '';
    }
    return currentPath.startsWith(path) && path !== '/';
  };

  useEffect(() => {
    setOpenGroup(getGroupForRoute({ pathname, search }));
  }, [pathname, search]);

  useLayoutEffect(() => {
    const sidebar = sidebarRef.current;
    if (!sidebar || isMobile) return undefined;
    const savedPosition = Number(sessionStorage.getItem('bsiq-sidebar-scroll') || 0);
    const frame = window.requestAnimationFrame(() => {
      sidebar.scrollTop = savedPosition;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isMobile, openGroup]);

  const preserveScrollPosition = () => {
    if (sidebarRef.current && !isMobile) {
      sessionStorage.setItem('bsiq-sidebar-scroll', String(sidebarRef.current.scrollTop));
    }
  };

  useEffect(() => {
    const fetchBadges = async () => {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      try {
        const [amRes, intRes, postAwardRes] = await Promise.all([
          fetch(`${API_URL}/api/amendments/unseen`, { headers }),
          fetch(`${API_URL}/api/intelligence/early-signals`, { headers }),
          fetch(`${API_URL}/api/post-award/alerts/unread-count`, { headers }),
        ]);
        const amJson = amRes.ok ? await amRes.json() : { count: 0 };
        const intJson = intRes.ok ? await intRes.json() : { new_since_last_visit: 0 };
        const postAwardJson = postAwardRes.ok ? await postAwardRes.json() : { count: 0 };
        setBadges({
          amendments: amJson.count || 0,
          intelligence: intJson.new_since_last_visit || 0,
          post_award: postAwardJson.count || 0,
        });
      } catch (e) {
        // Sidebar badges should never block navigation rendering.
      }
    };

    fetchBadges();
    const timer = setInterval(fetchBadges, 60 * 1000);
    return () => clearInterval(timer);
  }, [location.pathname]);

  const filteredSections = useMemo(() => {
    const isAdmin = (user?.role || 'admin') === 'admin';
    const isInternal = Boolean(user?.is_bsiq_internal || user?.is_demo_mode);
    const isInfraAdmin = INFRA_ADMIN_EMAILS.includes((user?.email || '').toLowerCase());

    return sidebarSections
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => {
          if (item.restrictedForViewer && isViewer) return false;
          if (item.path?.startsWith('/admin') && !isInternal) return false;
          if (item.adminOnly && !isAdmin) return false;
          if (item.infraAdminOnly && !isInfraAdmin) return false;
          return true;
        }),
      }))
      .filter((section) => section.items.length > 0);
  }, [isViewer, user]);

  const toggle = (groupName) => {
    setOpenGroup((prev) => (prev === groupName ? null : groupName));
  };

  const closeMobile = () => {
    preserveScrollPosition();
    if (isMobile && onMobileClose) onMobileClose();
  };

  const shellWidth = isMobile && !mobileOpen ? 56 : 244;

  return (
    <>
      {isMobile && mobileOpen && (
        <button
          type="button"
          aria-label="Close navigation overlay"
          className="fixed inset-0 z-[90] cursor-default"
          style={{ background: 'rgba(15, 23, 42, 0.45)' }}
          onClick={onMobileClose}
        />
      )}

      <aside
        ref={sidebarRef}
        onScroll={preserveScrollPosition}
        className="fixed left-0 top-0 z-[100] flex h-screen flex-col"
        style={{
          width: `${shellWidth}px`,
          background: '#091426',
          borderRight: '1px solid rgba(255,255,255,0.08)',
          overflowY: 'auto',
          fontFamily: "'Public Sans', sans-serif",
          transition: 'width 200ms ease, box-shadow 200ms ease',
          boxShadow: isMobile && mobileOpen ? '0 20px 60px rgba(15, 23, 42, 0.25)' : 'none',
        }}
        data-testid="global-sidebar"
      >
        {isMobile && !mobileOpen ? (
          <div className="flex h-full flex-col items-center gap-4 py-4">
            <button
              type="button"
              onClick={onMobileToggle}
              aria-label="Open navigation"
              className="flex h-10 w-10 items-center justify-center rounded-md"
              style={{ color: '#FFFFFF' }}
            >
              <Menu className="h-5 w-5" />
            </button>
            <Link to="/command-center" aria-label="BidsignalIQ">
              <img src="/bidsignaliq-logo.png?v=20261021" alt="" className="h-8 w-auto" />
            </Link>
          </div>
        ) : (
          <>
            <div className="px-4 pb-3 pt-4">
              <div className="mb-4 flex items-center justify-between gap-3">
                <Link to="/command-center" onClick={closeMobile} className="inline-flex min-w-0 items-center gap-2" data-testid="sidebar-logo">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white">
                    <img src="/bidsignaliq-logo.png?v=20261021" alt="" className="h-8 w-auto" />
                  </span>
                  <span className="truncate text-[16px] font-semibold tracking-[-0.01em]" style={{ color: '#FFFFFF' }}>
                    BidsignalIQ
                  </span>
                </Link>
                {isMobile && (
                  <button
                    type="button"
                    onClick={onMobileClose}
                    aria-label="Close navigation"
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md"
                    style={{ color: '#FFFFFF' }}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

            </div>

            <nav className="flex-1 pb-4" aria-label="Primary">
              {filteredSections.map((section) => (
                <div key={section.label}>
                  <div style={SECTION_LABEL_STYLE}>{section.label}</div>
                  {section.items.map((item) => {
                    if (item.type === 'group') {
                      const activeChildPath = item.children.some((child) => {
                        if (child.type === 'label') return false;
                        return isActive(child.path.split('?')[0], child.exact, child.queryMatch);
                      });
                      return (
                        <AccordionItem
                          key={item.key}
                          item={item}
                          isOpen={openGroup === item.key}
                          activeChildPath={activeChildPath}
                          isActive={isActive}
                          onToggle={() => toggle(item.key)}
                          onNavigate={closeMobile}
                        />
                      );
                    }

                    const active = isActive(item.path, item.exact, item.queryMatch);
                    return (
                      <NavLinkItem
                        key={item.path}
                        item={item}
                        active={active}
                        badgeCount={item.badgeKey ? badges[item.badgeKey] || 0 : 0}
                        onClick={closeMobile}
                      />
                    );
                  })}
                </div>
              ))}
            </nav>

          </>
        )}
      </aside>
    </>
  );
};

export default Sidebar;
